/**
 * Scan active sheet used range for OptLab option entities
 * Extracts OCC symbols from entity properties (no formula parsing)
 */

/* global Excel */

/* eslint-disable @typescript-eslint/no-explicit-any */

import { runExcel } from "../../excel/runExcel";
import { isOptLabOptionEntityCellValue, getOccSymbolFromEntityCellValue } from "../excelEntities/optionEntity";
import { parseOccSymbol } from "../symbols/occOptionSymbol";
import { toA1 } from "./a1";

export type OptionEntityCellRef = { address: string; occSymbol: string };

/**
 * Pure helper: extract option entity refs from valuesAsJson (row-major).
 * Uses same entity detection and OCC validation as the async scanner.
 */
export function extractOptionEntityRefsFromValuesAsJson(
  valuesAsJson: any[][],
  rangeRowIndex0: number,
  rangeColumnIndex0: number
): OptionEntityCellRef[] {
  const items: OptionEntityCellRef[] = [];

  for (let r = 0; r < valuesAsJson.length; r++) {
    const rowData = valuesAsJson[r];
    if (!Array.isArray(rowData)) {
      continue;
    }
    for (let c = 0; c < rowData.length; c++) {
      const cellValue = rowData[c];
      if (!isOptLabOptionEntityCellValue(cellValue)) {
        continue;
      }
      const occSymbol = getOccSymbolFromEntityCellValue(cellValue);
      if (!occSymbol || typeof occSymbol !== "string" || occSymbol.trim() === "") {
        continue;
      }
      try {
        parseOccSymbol(occSymbol.replace(/\s+/g, "").toUpperCase());
      } catch {
        continue;
      }
      const rowIndex0 = rangeRowIndex0 + r;
      const colIndex0 = rangeColumnIndex0 + c;
      const address = toA1(rowIndex0, colIndex0);
      items.push({ address, occSymbol: occSymbol.replace(/\s+/g, "").toUpperCase() });
    }
  }

  return items;
}

/**
 * Scan the active worksheet used range for option entities.
 * Returns items (address + occSymbol), scanned cell count, and used range address.
 * Note: isNullObject is a property on the object, not loaded; check after sync.
 */
export async function scanActiveSheetForOptionEntities(): Promise<{
  items: OptionEntityCellRef[];
  scannedCellCount: number;
  usedRangeAddress?: string;
}> {
  return runExcel(async (context) => {
    const sheet = context.workbook.worksheets.getActiveWorksheet();
    const range = sheet.getUsedRangeOrNullObject();
    range.load(["address", "valuesAsJson", "rowIndex", "columnIndex"]);
    await context.sync();

    if (range.isNullObject) {
      return { items: [], scannedCellCount: 0, usedRangeAddress: undefined };
    }

    const valuesAsJson = range.valuesAsJson as any[][];
    if (!Array.isArray(valuesAsJson)) {
      return { items: [], scannedCellCount: 0, usedRangeAddress: range.address };
    }

    const rowCount = valuesAsJson.length;
    const colCount = valuesAsJson.reduce((max, row) => Math.max(max, Array.isArray(row) ? row.length : 0), 0);
    const scannedCellCount = rowCount * colCount;

    const rowIndex0 = range.rowIndex as number;
    const colIndex0 = range.columnIndex as number;
    const items = extractOptionEntityRefsFromValuesAsJson(valuesAsJson, rowIndex0, colIndex0);

    return {
      items,
      scannedCellCount,
      usedRangeAddress: range.address,
    };
  });
}
